import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle, XCircle, Package, HardDrive,
  Clock, Hash, MapPin, ShoppingCart, Truck, RotateCcw,
  DollarSign, Calculator, FileText, BarChart3, RefreshCw,
  Users, Building2, Settings, Lock, Layers, ClipboardList
} from 'lucide-react';
import stocktakeOfflineService from '../services/stocktakeOfflineService';
import { format } from 'date-fns';

/**
 * Offline Package Diagnostics v3 — proves the contents of the THICK
 * offline package. Shows every dataset category with record counts,
 * previous locked session reference, and supplier/site data presence.
 */
export default function OfflinePackageDiagnostics({ sessionId }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [packageSize, setPackageSize] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await stocktakeOfflineService.loadOfflinePackage(Number(sessionId));
    setRecord(r);
    // Estimate package size from JSON serialization
    if (r?.package) {
      try {
        const bytes = new Blob([JSON.stringify(r)]).size;
        setPackageSize(bytes);
      } catch { setPackageSize(null); }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [sessionId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Checking offline package...
        </CardContent>
      </Card>
    );
  }

  if (!record || !record.package) {
    return (
      <Card>
        <CardContent className="py-8 text-center" data-testid="offline-pkg-not-downloaded">
          <XCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
          <p className="text-sm font-medium text-slate-700">Not Downloaded</p>
          <p className="text-xs text-muted-foreground mt-1">
            This session has not been downloaded for offline use.
          </p>
        </CardContent>
      </Card>
    );
  }

  const pkg = record.package;
  const session = pkg.session || {};
  const prevLocked = pkg.previous_locked_session;

  // Count delivery/credit lines
  const totalDeliveryLines = (pkg.deliveries || []).reduce((s, d) => s + (d.lines || []).length, 0);
  const totalCreditLines = (pkg.credits || []).reduce((s, c) => s + (c.lines || []).length, 0);
  const postedDeliveries = (pkg.deliveries || []).filter(d => d.status === 'posted').length;
  const draftDeliveries = (pkg.deliveries || []).filter(d => d.status === 'draft').length;
  const postedCredits = (pkg.credits || []).filter(c => c.status === 'posted').length;
  const draftCredits = (pkg.credits || []).filter(c => c.status === 'draft').length;
  const supplierProductLinks = (pkg.suppliers || []).reduce((s, sup) => s + (sup.product_links || []).length, 0);

  const checks = [
    // Session & Count
    { group: 'Session', label: 'Session Header', icon: FileText, count: session.id ? 1 : 0, required: true, detail: session.session_reference },
    { group: 'Session', label: 'Count Sheet Lines', icon: BarChart3, count: (pkg.count_sheet_lines || []).length, required: true },
    { group: 'Session', label: 'Count Entries', icon: Hash, count: (pkg.count_entries || []).length, required: false, detail: 'Existing entries at download' },

    // Masters
    { group: 'Masters', label: 'Products (Full Org)', icon: Package, count: (pkg.products || []).length, required: true, detail: 'All org products' },
    { group: 'Masters', label: 'Unit Definitions', icon: Hash, count: (pkg.products || []).reduce((sum, p) => sum + (p.units || []).length, 0), required: true },
    { group: 'Masters', label: 'Yield Definitions', icon: Hash, count: (pkg.products || []).reduce((sum, p) => sum + (p.yields || []).length, 0), required: false },
    { group: 'Masters', label: 'Suppliers', icon: Users, count: (pkg.suppliers || []).length, required: true, detail: `${supplierProductLinks} product links` },

    // Site
    { group: 'Site', label: 'Site Header', icon: Building2, count: pkg.site ? 1 : 0, required: true, detail: pkg.site?.name },
    { group: 'Site', label: 'Site Settings', icon: Settings, count: pkg.site_settings ? 1 : 0, required: false },
    { group: 'Site', label: 'Inventory Mappings', icon: MapPin, count: (pkg.site_inventory || []).length, required: false, detail: 'Active site products' },
    { group: 'Site', label: 'Locations', icon: MapPin, count: (pkg.locations || []).length, required: true },
    { group: 'Site', label: 'Count Sheet Templates', icon: ClipboardList, count: (pkg.count_sheet_templates || []).length, required: false },

    // Previous Locked Session
    { group: 'Prior Audit', label: 'Previous Locked Session', icon: Lock, count: prevLocked ? 1 : 0, required: false,
      detail: prevLocked ? `${prevLocked.session?.session_reference} (${prevLocked.session?.session_date})` : 'None found' },
    { group: 'Prior Audit', label: 'Prior Count Lines', icon: BarChart3, count: prevLocked ? (prevLocked.count_sheet_lines || []).length : 0, required: false },
    { group: 'Prior Audit', label: 'Prior Count Entries', icon: Hash, count: prevLocked ? (prevLocked.count_entries || []).length : 0, required: false },
    { group: 'Prior Audit', label: 'Prior Calc Snapshot', icon: Calculator, count: prevLocked?.calculation_snapshot ? 1 : 0, required: false,
      detail: prevLocked?.calculation_snapshot ? `v${prevLocked.calculation_snapshot.version}` : null },
    { group: 'Prior Audit', label: 'Prior Snapshot Lines', icon: Layers, count: prevLocked ? (prevLocked.calculation_snapshot_lines || []).length : 0, required: false },

    // Transactions
    { group: 'Transactions', label: 'Deliveries', icon: Truck, count: (pkg.deliveries || []).length, required: false,
      detail: `${postedDeliveries} posted, ${draftDeliveries} draft, ${totalDeliveryLines} lines` },
    { group: 'Transactions', label: 'Credits', icon: RotateCcw, count: (pkg.credits || []).length, required: false,
      detail: `${postedCredits} posted, ${draftCredits} draft, ${totalCreditLines} lines` },
    { group: 'Transactions', label: 'Opening Stock', icon: DollarSign, count: (pkg.opening_stock || []).length, required: false, detail: 'Prior period basis' },
    { group: 'Transactions', label: 'Sales Entries', icon: ShoppingCart, count: (pkg.sales_entries || []).length, required: false },
    { group: 'Transactions', label: 'Adjustment Entries', icon: RotateCcw, count: (pkg.adjustment_entries || []).length, required: false },

    // Reference
    { group: 'Reference', label: 'Product Costs (WAC)', icon: DollarSign, count: (pkg.product_costs || []).length, required: true, detail: 'Costing basis' },
    { group: 'Reference', label: 'Costing Policy', icon: Calculator, count: pkg.costing_policy ? 1 : 0, required: true, detail: pkg.costing_policy?.costing_method },
    { group: 'Reference', label: 'Org Settings (VAT)', icon: Settings, count: pkg.org_stocktaking_settings ? 1 : 0, required: false,
      detail: pkg.org_stocktaking_settings ? `VAT ${pkg.org_stocktaking_settings.default_vat_rate}%` : null },
    { group: 'Reference', label: 'Calculation Snapshot', icon: Calculator, count: pkg.calculation_snapshot ? 1 : 0, required: false, detail: 'Current calc if exists' },
  ];

  const allRequired = checks.filter(c => c.required).every(c => c.count > 0);
  const groups = [...new Set(checks.map(c => c.group))];
  const visibleChecks = expanded ? checks : checks.filter(c => c.required || c.count > 0);

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const sizeColor = packageSize
    ? packageSize > 5 * 1024 * 1024 ? 'text-amber-600' : 'text-emerald-600'
    : '';

  return (
    <Card data-testid="offline-pkg-diagnostics">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <HardDrive className="h-4 w-4" />
          Offline Package Diagnostics
          <Badge className="text-[10px] font-mono" variant="outline">v{pkg.schema_version}</Badge>
          {allRequired ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
              <CheckCircle className="h-3 w-3 mr-0.5" />Ready
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-[10px]">
              <XCircle className="h-3 w-3 mr-0.5" />Incomplete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Metadata */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          <div className="p-2 bg-slate-50 rounded">
            <p className="text-muted-foreground">Package Version</p>
            <p className="font-semibold" data-testid="pkg-schema-version">v{pkg.schema_version}</p>
          </div>
          <div className="p-2 bg-slate-50 rounded">
            <p className="text-muted-foreground">Package Size</p>
            <p className={`font-semibold ${sizeColor}`} data-testid="pkg-size">
              {formatSize(packageSize)}
            </p>
          </div>
          <div className="p-2 bg-slate-50 rounded">
            <p className="text-muted-foreground">Downloaded</p>
            <p className="font-semibold" data-testid="pkg-downloaded-at">
              {record.downloaded_at ? format(new Date(record.downloaded_at), 'd MMM yyyy HH:mm') : '—'}
            </p>
          </div>
          <div className="p-2 bg-slate-50 rounded">
            <p className="text-muted-foreground">Last Synced</p>
            <p className="font-semibold">
              {record.last_synced_at ? format(new Date(record.last_synced_at), 'd MMM yyyy HH:mm') : '—'}
            </p>
          </div>
          <div className="p-2 bg-slate-50 rounded">
            <p className="text-muted-foreground">Sync State</p>
            <p className="font-semibold capitalize">{record.sync_state || 'unknown'}</p>
          </div>
        </div>

        {/* Session info */}
        <div className="p-2 bg-slate-50 rounded text-xs">
          <p className="text-muted-foreground mb-1">Session</p>
          <p className="font-semibold">{session.session_reference} — {session.site_name}</p>
          <p className="text-muted-foreground">
            {session.session_type} | {session.session_date}
            {session.period_from && ` | Period: ${session.period_from} to ${session.period_to}`}
          </p>
        </div>

        {/* Dataset checklist — grouped */}
        <div className="space-y-2">
          {groups.map(group => {
            const groupChecks = visibleChecks.filter(c => c.group === group);
            if (groupChecks.length === 0) return null;
            return (
              <div key={group}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5 px-2">{group}</p>
                {groupChecks.map((check) => {
                  const Icon = check.icon;
                  const ok = check.count > 0;
                  return (
                    <div
                      key={check.label}
                      className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50 text-xs"
                      data-testid={`pkg-check-${check.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        {ok ? (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        ) : check.required ? (
                          <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        ) : (
                          <span className="h-3.5 w-3.5 rounded-full border border-slate-300 flex-shrink-0" />
                        )}
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className={ok ? 'text-slate-700' : 'text-muted-foreground'}>
                          {check.label}
                          {check.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {check.detail && <span className="text-muted-foreground max-w-[200px] truncate">{check.detail}</span>}
                        <Badge variant={ok ? 'secondary' : 'outline'} className="text-[10px] tabular-nums min-w-[28px] justify-center">
                          {check.count}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={load} className="text-xs" data-testid="refresh-diagnostics-btn">
            <RefreshCw className="h-3 w-3 mr-1" />Refresh
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
            className="text-xs"
            data-testid="toggle-all-checks-btn"
          >
            {expanded ? 'Show relevant only' : `Show all (${checks.length})`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
